import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Grid,
  Collapse,
  Typography,
  createStyles,
  makeStyles,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import SearchIcon from '@material-ui/icons/Search';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import Button from 'src/components/Button';
import { Theme } from 'src/theme/types/createPalette';
import {
  getCampaignList,
  updateCampaignFolder,
  renameCampaignFolder,
} from 'src/apis/campaign';
import useAuth from 'src/hooks/useAuth';
import useCampaign from 'src/hooks/useCampaign';
import useNotification from 'src/hooks/useNotification';
import useReceivedShares from 'src/hooks/useReceivedShares';
import useOwnedShares from 'src/hooks/useOwnedShares';
import { CampaignProps } from 'src/types/campaign';
import { EditCampaignModal } from 'src/modals/EditCampaignModal';
import { DeleteCampaignModal } from 'src/modals/DeleteCampaignModal';
import { NewReportModal } from 'src/modals/NewReportModal';
import { ShareModal, ShareTarget } from 'src/modals/ShareModal';
import {
  LeaveShareModal,
  LeaveShareConfirmState,
} from 'src/modals/LeaveShareModal';
import {
  canShareResources,
  getReceivedItemSharesForIds,
  isCampaignInReceivedFolderShare,
  leaveFolderShareWithItemShares,
  leaveReceivedShareById,
  matchReceivedFolderShare,
  matchReceivedShare,
} from 'src/utils/share';
import {
  buildFolderTree,
  collectCampaignsUnderPath,
  CAMPAIGN_DND_TYPE,
  FOLDER_DND_TYPE,
  flattenFolderTree,
  folderParentDroppableId,
  getChildFolderNames,
  getFolderLeafName,
  getParentPath,
  isDescendantPath,
  isPathEqual,
  isUnderPath,
  joinFolderPath,
  normalizeFolderPath,
  parseFolderDraggableId,
  rewritePathPrefix,
  splitFolderPath,
  uniqueSiblingFolderName,
} from 'src/utils/campaignFolderPath';

import { CampaignItem } from './CampaignItem';
import { CampaignFolder } from './CampaignFolder';

const UNCATEGORIZED_DROPPABLE_ID = 'Uncategorized Campaigns';

export const useStyles: any = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      height: 'calc(100vh - 50px)',
    },
    mainContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
      maxHeight: '100%',
      overflow: 'auto',
    },
    addBtn: {
      background: '#ffffff',
      color: '#000000',
      border: '1px solid #000000',
      fontWeight: 'normal',
      fontSize: 16,
      '&.MuiButton-containedPrimary:hover': {
        backgroundColor: '#5EA1F2',
        color: '#fff',
      },
    },
    searchBar: {
      width: '100%',
      border: '1px solid #C3C3C7',
      padding: '2px 8px',
      borderRadius: 36,

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    collapseContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 24,
      padding: '20px 12px',
      paddingRight: 0,
    },
    collapseTitle: {
      fontSize: 15,
      fontWeight: 500,
      flex: 1,
    },
    collapseCounter: {
      border: '1px solid #000000',
      borderRadius: 2,
      fontSize: 12,
      lineHeight: '12px',
      padding: 4,
      marginRight: 8,
    },
  })
);

interface CampaignListSidebarProps {}

const CampaignListSidebar: FC<CampaignListSidebarProps> = () => {
  const classes = useStyles();
  const {
    campaigns,
    setCampaigns,
    selectedCampaignId,
    setSelectedCampaignId,
    setOpenCreateModal,
  } = useCampaign();
  const { user } = useAuth();
  const isViewer = user?.role === 'view';
  const canShare = canShareResources(user?.role);
  const { handleSuccess, handleError } = useNotification();
  const {
    shares: receivedShares,
    isReady: receivedSharesReady,
    refreshReceivedShares,
    sharedCampaignIds,
    sharedCampaignFolderKeys,
  } = useReceivedShares();
  const {
    refreshOwnedShares,
    sharedOutCampaignIds,
    sharedOutCampaignFolderKeys,
  } = useOwnedShares();

  const [openUncategorized, setOpenUncategorized] = useState<boolean>(true);
  const [openFolders, setOpenFolders] = useState<boolean>(true);
  const [openShared, setOpenShared] = useState<boolean>(true);
  const [openRecentlyViewed, setOpenRecentlyViewed] = useState<boolean>(true);
  const [openSharedFolderPaths, setOpenSharedFolderPaths] = useState<
    Set<string>
  >(() => new Set());
  const [emptyFolderPaths, setEmptyFolderPaths] = useState<string[]>([]);
  const [openFolderPaths, setOpenFolderPaths] = useState<Set<string>>(
    () => new Set()
  );
  const [activeCampaign, setActiveCampaign] = useState<CampaignProps | null>(
    null
  );
  const [searchText, setSearchText] = useState<string>('');
  const [editCampaign, setEditCampaign] = useState<CampaignProps | null>(null);
  const [deleteCampaignTarget, setDeleteCampaignTarget] =
    useState<CampaignProps | null>(null);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [leaveConfirm, setLeaveConfirm] =
    useState<LeaveShareConfirmState | null>(null);

  const handleShareFolder = (folderPath: string, name: string) => {
    setShareTarget({
      type: 'campaignFolder',
      name,
      resourceId: folderPath,
    });
  };

  const handleShareCampaign = (campaign: CampaignProps) => {
    setShareTarget({
      type: 'campaign',
      name: campaign.name,
      resourceId: campaign.id,
    });
  };

  const fetchCampaigns = useCallback(async () => {
    const response = await getCampaignList({
      page: 0,
      pageSize: 500,
      sortBy: 'createdAt',
      sortOrder: 'DESC',
    });
    setCampaigns(response.campaigns);
  }, [setCampaigns]);

  const handleLeaveCampaign = (campaign: CampaignProps) => {
    const share = matchReceivedShare(receivedShares, 'CAMPAIGN', campaign.id);
    if (!share) {
      handleError(null, 'No matching share found for this campaign.');
      return;
    }

    setLeaveConfirm({
      title: 'Leave shared campaign',
      message: `Leave shared campaign "${campaign.name}"? Your organization will lose access.`,
      onConfirm: async () => {
        try {
          await leaveReceivedShareById(share.id);
          handleSuccess(`Left shared campaign "${campaign.name}".`);
          if (selectedCampaignId === campaign.id) {
            setSelectedCampaignId(null);
          }
          await Promise.all([fetchCampaigns(), refreshReceivedShares()]);
        } catch (error) {
          handleError(error, 'Unable to leave this shared campaign.');
          throw error;
        }
      },
    });
  };

  const handleLeaveFolder = (folderPath: string, name: string) => {
    const share = matchReceivedFolderShare(
      receivedShares,
      'CAMPAIGN_FOLDER',
      folderPath
    );
    if (!share) {
      handleError(null, 'No matching share found for this folder.');
      return;
    }

    const folderCampaigns = campaigns.filter((item) =>
      isUnderPath(item.folder ?? '', folderPath)
    );
    const itemShares = getReceivedItemSharesForIds(
      receivedShares,
      'CAMPAIGN',
      folderCampaigns.map((item) => item.id)
    );

    setLeaveConfirm({
      title: 'Leave shared folder',
      message: itemShares.length
        ? `Leave shared folder "${name}"? This also removes access from campaigns that were shared individually in this folder.`
        : `Leave shared folder "${name}"? Your organization will lose access to campaigns in this folder.`,
      onConfirm: async () => {
        try {
          await leaveFolderShareWithItemShares(share, itemShares);
          handleSuccess(`Left shared folder "${name}".`);
          if (
            selectedCampaignId &&
            campaigns.some(
              (item) =>
                item.id === selectedCampaignId &&
                item.isShared &&
                isUnderPath(item.folder ?? '', folderPath)
            )
          ) {
            setSelectedCampaignId(null);
          }
          await Promise.all([fetchCampaigns(), refreshReceivedShares()]);
        } catch (error) {
          handleError(error, 'Unable to leave this shared folder.');
          throw error;
        }
      },
    });
  };

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Poll campaign list every 60s for updated scoringStatus.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchCampaigns();
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [fetchCampaigns]);

  // Drop empty-folder placeholders once a campaign (or nested path) covers them.
  useEffect(() => {
    setEmptyFolderPaths((prev) =>
      prev.filter((emptyPath) => {
        const stillEmpty = !campaigns.some((item) =>
          isUnderPath(item.folder ?? '', emptyPath)
        );
        return stillEmpty;
      })
    );
  }, [campaigns]);

  const ownedCampaigns = useMemo(
    () => campaigns.filter((item) => !item.isShared),
    [campaigns]
  );

  const folderSharedCampaigns = useMemo(
    () =>
      campaigns.filter(
        (item) =>
          item.isShared &&
          isCampaignInReceivedFolderShare(item.folder, sharedCampaignFolderKeys)
      ),
    [campaigns, sharedCampaignFolderKeys]
  );

  const sharedLooseCampaigns = useMemo(
    () =>
      campaigns.filter(
        (item) =>
          item.isShared &&
          !isCampaignInReceivedFolderShare(
            item.folder,
            sharedCampaignFolderKeys
          )
      ),
    [campaigns, sharedCampaignFolderKeys]
  );

  const ownedEmptyFolderPaths = useMemo(
    () =>
      emptyFolderPaths.filter(
        (path) =>
          !isCampaignInReceivedFolderShare(path, sharedCampaignFolderKeys)
      ),
    [emptyFolderPaths, sharedCampaignFolderKeys]
  );

  const { roots: folderTree, uncategorized: uncategorizedCampaigns } = useMemo(
    () => buildFolderTree(ownedCampaigns, ownedEmptyFolderPaths),
    [ownedCampaigns, ownedEmptyFolderPaths]
  );

  const { roots: sharedFolderTree } = useMemo(
    () => buildFolderTree(folderSharedCampaigns),
    [folderSharedCampaigns]
  );

  const flatFolders = useMemo(
    () => flattenFolderTree(folderTree, openFolderPaths),
    [folderTree, openFolderPaths]
  );

  const flatSharedFolders = useMemo(
    () => flattenFolderTree(sharedFolderTree, openSharedFolderPaths),
    [sharedFolderTree, openSharedFolderPaths]
  );

  // Wait for /share/received so folder shares aren't briefly shown as loose items.
  const hasSharedContent =
    receivedSharesReady &&
    (flatSharedFolders.length > 0 || sharedLooseCampaigns.length > 0);

  const toggleFolderOpen = (path: string) => {
    setOpenFolderPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const toggleSharedFolderOpen = (path: string) => {
    setOpenSharedFolderPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  useEffect(() => {
    if (selectedCampaignId) {
      const c = campaigns.find((item) => item.id === selectedCampaignId);
      setActiveCampaign(c || campaigns[0] || null);
    } else {
      setActiveCampaign(campaigns[0] || null);
    }
  }, [campaigns, selectedCampaignId]);

  const searchLower = searchText.trim().toLowerCase();
  const matchesSearch = (c: CampaignProps) =>
    c.name?.toLowerCase().includes(searchLower);

  const handleNewFolder = (parentPath = '') => {
    const siblingNames = getChildFolderNames(folderTree, parentPath);
    const leafName = uniqueSiblingFolderName(siblingNames);
    const newPath = parentPath
      ? joinFolderPath([...splitFolderPath(parentPath), leafName])
      : leafName;
    setEmptyFolderPaths((prev) =>
      prev.includes(newPath) ? prev : [...prev, newPath]
    );
    if (parentPath) {
      setOpenFolderPaths((prev) => new Set(prev).add(parentPath));
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { source, destination, draggableId, combine } = result;

    const draggedFolderPath = parseFolderDraggableId(draggableId);
    if (draggedFolderPath !== null) {
      // Shared folders are not movable.
      if (
        isCampaignInReceivedFolderShare(
          draggedFolderPath,
          sharedCampaignFolderKeys
        )
      ) {
        return;
      }

      // Drop onto another folder → nest inside it (isCombineEnabled).
      if (combine) {
        const targetPath = parseFolderDraggableId(combine.draggableId);
        if (
          targetPath &&
          !isCampaignInReceivedFolderShare(targetPath, sharedCampaignFolderKeys)
        ) {
          await handleMoveFolder(draggedFolderPath, targetPath);
        }
        return;
      }

      if (!destination) {
        return;
      }

      // Drop between folders → become a sibling of the anchor item
      // (same parent). This also covers moving a nested folder to root.
      const withoutDragged = flatFolders.filter(
        (item) => !isPathEqual(item.node.path, draggedFolderPath)
      );
      if (!withoutDragged.length) {
        await handleMoveFolder(draggedFolderPath, '');
        return;
      }
      const anchorIndex = Math.min(
        destination.index,
        withoutDragged.length - 1
      );
      const anchor = withoutDragged[anchorIndex];
      await handleMoveFolder(
        draggedFolderPath,
        getParentPath(anchor.node.path)
      );
      return;
    }

    if (!destination || !source) {
      return;
    }

    if (destination.droppableId === source.droppableId) {
      return;
    }

    const sourcePath =
      source.droppableId === UNCATEGORIZED_DROPPABLE_ID
        ? ''
        : normalizeFolderPath(source.droppableId);
    const destinationPath =
      destination.droppableId === UNCATEGORIZED_DROPPABLE_ID
        ? ''
        : normalizeFolderPath(destination.droppableId);

    if (
      isCampaignInReceivedFolderShare(destinationPath, sharedCampaignFolderKeys)
    ) {
      return;
    }

    const sourceCampaigns = (
      sourcePath === ''
        ? uncategorizedCampaigns
        : ownedCampaigns.filter(
            (item) => normalizeFolderPath(item.folder ?? '') === sourcePath
          )
    ).filter(matchesSearch);

    const dragItem = sourceCampaigns[source.index];
    if (!dragItem || dragItem.isShared) {
      return;
    }

    const previousCampaigns = campaigns;
    setCampaigns(
      campaigns.map((item) =>
        item.id === dragItem.id ? { ...item, folder: destinationPath } : item
      )
    );

    try {
      await updateCampaignFolder(dragItem.id, destinationPath);
      handleSuccess('Campaign folder updated');
      await fetchCampaigns();
    } catch (error) {
      setCampaigns(previousCampaigns);
      handleError(error, 'Could not update campaign folder');
      await fetchCampaigns();
    }
  };

  const handleMoveFolder = async (
    folderPath: string,
    newParentPath: string
  ) => {
    const oldPath = normalizeFolderPath(folderPath);
    const parentPath = normalizeFolderPath(newParentPath);

    if (!oldPath) {
      return;
    }
    // Cannot move a folder into itself or one of its descendants.
    if (
      isPathEqual(oldPath, parentPath) ||
      isDescendantPath(parentPath, oldPath)
    ) {
      return;
    }
    // Same parent — order is not persisted.
    if (isPathEqual(getParentPath(oldPath), parentPath)) {
      return;
    }

    const leaf = getFolderLeafName(oldPath);
    const siblingNames = getChildFolderNames(folderTree, parentPath);
    const uniqueLeaf = uniqueSiblingFolderName(siblingNames, leaf);
    const newPath = parentPath
      ? joinFolderPath([...splitFolderPath(parentPath), uniqueLeaf])
      : uniqueLeaf;

    if (isPathEqual(oldPath, newPath)) {
      return;
    }

    const items = collectCampaignsUnderPath(campaigns, oldPath);
    const previousCampaigns = campaigns;
    const previousEmpty = emptyFolderPaths;

    if (parentPath) {
      setOpenFolderPaths((prev) => new Set(prev).add(parentPath));
    }

    setCampaigns(
      campaigns.map((item) => {
        if (!isUnderPath(item.folder ?? '', oldPath)) {
          return item;
        }
        return {
          ...item,
          folder: rewritePathPrefix(item.folder ?? '', oldPath, newPath),
        };
      })
    );
    setEmptyFolderPaths((prev) => {
      const next = prev.map((emptyPath) =>
        isUnderPath(emptyPath, oldPath)
          ? rewritePathPrefix(emptyPath, oldPath, newPath)
          : emptyPath
      );
      if (items.length === 0 && !next.some((p) => isPathEqual(p, newPath))) {
        next.push(newPath);
      }
      return Array.from(
        new Set(next.map((p) => normalizeFolderPath(p)).filter(Boolean))
      );
    });

    try {
      if (items.length) {
        // Use folders/rename so CAMPAIGN_FOLDER share grants move with the path.
        await renameCampaignFolder(oldPath, newPath);
      }
      handleSuccess('Folder moved');
      await fetchCampaigns();
    } catch (error) {
      setCampaigns(previousCampaigns);
      setEmptyFolderPaths(previousEmpty);
      handleError(error, 'Could not move folder');
      await fetchCampaigns();
    }
  };

  const handleDeleteFolder = async (folderPath: string) => {
    const path = normalizeFolderPath(folderPath);
    const items = collectCampaignsUnderPath(campaigns, path);
    const parentPath = getParentPath(path);
    const previousCampaigns = campaigns;
    const previousEmpty = emptyFolderPaths;

    setCampaigns(
      campaigns.map((item) => {
        if (!isUnderPath(item.folder ?? '', path)) {
          return item;
        }
        return {
          ...item,
          folder: rewritePathPrefix(item.folder ?? '', path, parentPath),
        };
      })
    );
    setEmptyFolderPaths((prev) => {
      const next = prev
        .map((emptyPath) =>
          isUnderPath(emptyPath, path)
            ? rewritePathPrefix(emptyPath, path, parentPath)
            : emptyPath
        )
        .filter((emptyPath) => Boolean(normalizeFolderPath(emptyPath)));
      return Array.from(new Set(next));
    });

    try {
      await Promise.all(
        items.map((item) =>
          updateCampaignFolder(
            item.id,
            rewritePathPrefix(item.folder ?? '', path, parentPath)
          )
        )
      );
      handleSuccess('Folder removed');
      await fetchCampaigns();
    } catch (error) {
      setCampaigns(previousCampaigns);
      setEmptyFolderPaths(previousEmpty);
      handleError(error, 'Could not update campaign folders');
      await fetchCampaigns();
    }
  };

  const handleRenameFolder = async (folderPath: string, newName: string) => {
    const path = normalizeFolderPath(folderPath);
    const segments = splitFolderPath(path);
    if (!segments.length) {
      return;
    }
    segments[segments.length - 1] = newName.trim();
    const newPath = joinFolderPath(segments);
    if (isPathEqual(path, newPath)) {
      return;
    }

    const items = collectCampaignsUnderPath(campaigns, path);
    const previousCampaigns = campaigns;
    const previousEmpty = emptyFolderPaths;

    setCampaigns(
      campaigns.map((item) => {
        if (!isUnderPath(item.folder ?? '', path)) {
          return item;
        }
        return {
          ...item,
          folder: rewritePathPrefix(item.folder ?? '', path, newPath),
        };
      })
    );
    setEmptyFolderPaths((prev) =>
      prev.map((emptyPath) =>
        isUnderPath(emptyPath, path)
          ? rewritePathPrefix(emptyPath, path, newPath)
          : emptyPath
      )
    );

    if (!items.length) {
      return;
    }

    try {
      await renameCampaignFolder(path, newPath);
      handleSuccess('Folder renamed');
      await fetchCampaigns();
    } catch (error) {
      setCampaigns(previousCampaigns);
      setEmptyFolderPaths(previousEmpty);
      handleError(error, 'Could not rename folder');
      await fetchCampaigns();
    }
  };

  const handleChangeSearchText = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearchText(event.target.value);
  };

  const handleCampaignUpdated = (updated: CampaignProps) => {
    setCampaigns(
      campaigns.map((item) =>
        item.id === updated.id ? { ...item, ...updated } : item
      )
    );
  };

  const handleCampaignDeleted = async (campaignId: string) => {
    if (selectedCampaignId === campaignId) {
      setSelectedCampaignId(null);
    }
    await fetchCampaigns();
  };

  return (
    <div className={classes.container}>
      <Typography
        variant='h5'
        style={{
          color: '#000',
          fontSize: 20,
          lineHeight: '24px',
          fontWeight: 500,
        }}
      >
        All Campaigns
      </Typography>
      <TextField
        InputLabelProps={{
          shrink: true,
          style: { fontWeight: 'normal' },
        }}
        InputProps={{
          placeholder: 'Search all campaigns...',
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon htmlColor='#858591' style={{ fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        value={searchText}
        onChange={handleChangeSearchText}
        className={classes.searchBar}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.mainContent}>
          <Grid container>
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              onClick={() => {
                setOpenRecentlyViewed(!openRecentlyViewed);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography className={classes.collapseTitle}>
                Recently Viewed
              </Typography>
              {openRecentlyViewed ? (
                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
              )}
            </Grid>
            <Collapse
              in={openRecentlyViewed}
              timeout='auto'
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Grid container className={classes.collapseContainer}>
                {activeCampaign && (
                  <CampaignItem
                    key={activeCampaign.id}
                    index={0}
                    campaign={activeCampaign}
                    noDraggable
                    showActions={!isViewer}
                    onSelectCampaign={setSelectedCampaignId}
                    onEditCampaign={
                      activeCampaign.isShared ? undefined : setEditCampaign
                    }
                    onDeleteCampaign={
                      activeCampaign.isShared
                        ? undefined
                        : setDeleteCampaignTarget
                    }
                    onShareCampaign={
                      canShare && !activeCampaign.isShared
                        ? handleShareCampaign
                        : undefined
                    }
                    onLeaveCampaign={
                      canShare &&
                      activeCampaign.isShared &&
                      sharedCampaignIds.has(activeCampaign.id) &&
                      !isCampaignInReceivedFolderShare(
                        activeCampaign.folder,
                        sharedCampaignFolderKeys
                      )
                        ? handleLeaveCampaign
                        : undefined
                    }
                    hideSharedLabel={Boolean(activeCampaign.isShared)}
                    isSharedOut={
                      !activeCampaign.isShared &&
                      sharedOutCampaignIds.has(activeCampaign.id)
                    }
                    sharedOutOrganizationCount={sharedOutCampaignIds.get(
                      activeCampaign.id
                    )}
                  />
                )}
              </Grid>
            </Collapse>
          </Grid>
          <Grid container>
            <Grid
              container
              item
              direction='row'
              justifyContent='space-between'
              alignItems='center'
              onClick={() => {
                setOpenFolders(!openFolders);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography className={classes.collapseTitle}>Folders</Typography>
              <Typography className={classes.collapseCounter}>
                {folderTree.length}
              </Typography>
              {openFolders ? (
                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
              )}
            </Grid>
            <Collapse
              in={openFolders}
              timeout='auto'
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Droppable
                droppableId={folderParentDroppableId('')}
                type={FOLDER_DND_TYPE}
                isCombineEnabled
              >
                {(folderProvided) => (
                  <Grid
                    container
                    className={classes.collapseContainer}
                    ref={folderProvided.innerRef}
                    {...folderProvided.droppableProps}
                  >
                    {flatFolders.map(({ node, depth }, index) => (
                      <CampaignFolder
                        key={node.path}
                        node={node}
                        index={index}
                        depth={depth}
                        isOpen={openFolderPaths.has(node.path)}
                        showActions={!isViewer}
                        onToggleOpen={toggleFolderOpen}
                        onDeleteFolder={handleDeleteFolder}
                        onRenameFolder={handleRenameFolder}
                        onAddSubfolder={handleNewFolder}
                        onSelectCampaign={setSelectedCampaignId}
                        onEditCampaign={setEditCampaign}
                        onDeleteCampaign={setDeleteCampaignTarget}
                        onShareFolder={canShare ? handleShareFolder : undefined}
                        onShareCampaign={
                          canShare ? handleShareCampaign : undefined
                        }
                        isSharedOut={
                          sharedOutCampaignFolderKeys.has(node.path) ||
                          sharedOutCampaignFolderKeys.has(node.name)
                        }
                        sharedOutOrganizationCount={
                          sharedOutCampaignFolderKeys.get(node.path) ??
                          sharedOutCampaignFolderKeys.get(node.name)
                        }
                        sharedOutCampaignIds={sharedOutCampaignIds}
                        matchesSearch={matchesSearch}
                      />
                    ))}
                    {folderProvided.placeholder}
                  </Grid>
                )}
              </Droppable>
            </Collapse>
          </Grid>
          <Droppable
            droppableId={UNCATEGORIZED_DROPPABLE_ID}
            type={CAMPAIGN_DND_TYPE}
          >
            {(provided) => (
              <Grid
                container
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                <Grid
                  container
                  item
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  onClick={() => {
                    setOpenUncategorized(!openUncategorized);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <Typography className={classes.collapseTitle}>
                    Uncategorized Campaigns
                  </Typography>
                  <Typography className={classes.collapseCounter}>
                    {uncategorizedCampaigns.filter(matchesSearch).length}
                  </Typography>
                  {openUncategorized ? (
                    <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                  )}
                </Grid>
                <Collapse
                  in={openUncategorized}
                  timeout='auto'
                  unmountOnExit
                  style={{ width: '100%' }}
                >
                  <Grid container className={classes.collapseContainer}>
                    {uncategorizedCampaigns
                      .filter(matchesSearch)
                      .map((campaign, index) => (
                        <CampaignItem
                          key={campaign.id}
                          index={index}
                          campaign={campaign}
                          showActions={!isViewer}
                          onSelectCampaign={setSelectedCampaignId}
                          onEditCampaign={setEditCampaign}
                          onDeleteCampaign={setDeleteCampaignTarget}
                          onShareCampaign={
                            canShare ? handleShareCampaign : undefined
                          }
                          isSharedOut={sharedOutCampaignIds.has(campaign.id)}
                          sharedOutOrganizationCount={sharedOutCampaignIds.get(
                            campaign.id
                          )}
                        />
                      ))}
                  </Grid>
                  {provided.placeholder}
                </Collapse>
              </Grid>
            )}
          </Droppable>
          {hasSharedContent && (
            <Grid container>
              <Grid
                container
                item
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                onClick={() => {
                  setOpenShared(!openShared);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Typography className={classes.collapseTitle}>
                  Shared
                </Typography>
                <Typography className={classes.collapseCounter}>
                  {sharedFolderTree.length +
                    sharedLooseCampaigns.filter(matchesSearch).length}
                </Typography>
                {openShared ? (
                  <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                ) : (
                  <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                )}
              </Grid>
              <Collapse
                in={openShared}
                timeout='auto'
                unmountOnExit
                style={{ width: '100%' }}
              >
                <Grid container className={classes.collapseContainer}>
                  {flatSharedFolders.map(({ node, depth }, index) => (
                    <CampaignFolder
                      key={`shared-${node.path}`}
                      node={node}
                      index={index}
                      depth={depth}
                      isOpen={openSharedFolderPaths.has(node.path)}
                      sharedSection
                      showActions={false}
                      onToggleOpen={toggleSharedFolderOpen}
                      onDeleteFolder={() => undefined}
                      onRenameFolder={() => undefined}
                      onAddSubfolder={() => undefined}
                      onSelectCampaign={setSelectedCampaignId}
                      onLeaveFolder={canShare ? handleLeaveFolder : undefined}
                      isReceivedFolderShare={
                        sharedCampaignFolderKeys.has(node.path) ||
                        sharedCampaignFolderKeys.has(node.name)
                      }
                      matchesSearch={matchesSearch}
                    />
                  ))}
                  {sharedLooseCampaigns
                    .filter(matchesSearch)
                    .map((campaign, index) => (
                      <CampaignItem
                        key={campaign.id}
                        index={index}
                        campaign={campaign}
                        noDraggable
                        showActions={false}
                        onSelectCampaign={setSelectedCampaignId}
                        onLeaveCampaign={
                          canShare && sharedCampaignIds.has(campaign.id)
                            ? handleLeaveCampaign
                            : undefined
                        }
                        hideSharedLabel
                      />
                    ))}
                </Grid>
              </Collapse>
            </Grid>
          )}
        </div>
      </DragDropContext>
      {!isViewer && (
        <>
          <Button
            title='+ Add new folder'
            color='primary'
            fontSize='sm'
            height={36}
            width='100%'
            className={classes.addBtn}
            onClick={() => handleNewFolder('')}
          />
          <Button
            title='+ New Campaign'
            color='primary'
            fontSize='sm'
            height={36}
            width='100%'
            className={classes.addBtn}
            onClick={() => setOpenCreateModal(true)}
          />
          <Button
            title='+ New Report'
            color='primary'
            fontSize='sm'
            height={36}
            width='100%'
            className={classes.addBtn}
            onClick={() => setOpenReportModal(true)}
          />
        </>
      )}
      <EditCampaignModal
        open={!!editCampaign}
        campaign={editCampaign}
        closeModal={() => setEditCampaign(null)}
        onUpdated={handleCampaignUpdated}
      />
      <DeleteCampaignModal
        open={!!deleteCampaignTarget}
        campaign={deleteCampaignTarget}
        closeModal={() => setDeleteCampaignTarget(null)}
        onDeleted={handleCampaignDeleted}
      />
      <NewReportModal
        open={openReportModal}
        closeModal={() => setOpenReportModal(false)}
      />
      <ShareModal
        open={!!shareTarget}
        target={shareTarget}
        closeModal={() => setShareTarget(null)}
        onSharesChanged={refreshOwnedShares}
      />
      <LeaveShareModal
        open={!!leaveConfirm}
        confirm={leaveConfirm}
        closeModal={() => setLeaveConfirm(null)}
      />
    </div>
  );
};

export default CampaignListSidebar;
